import {
  computed, ref, useContext,
} from '@nuxtjs/composition-api';
import { sanitizeUrl } from '~/helpers/sanitizeUrl';
import { Logger } from '~/helpers/logger';
import urlRewriteStoreViews from '~/modules/GraphQL/customQueries/urlRewriteStoreViews.gql';
import { Alternate } from '~/helpers/getMetaInfo';
import { STORE_URL } from '~/config/base';
import { UseMagentoMetaDataInterface } from '~/composables/useMagentoMetaData/useMagentoMetaData';

export function useMagentoMetaData(): UseMagentoMetaDataInterface {
  const context = useContext();
  const { path } = context.route.value;

  const magentoMetaUrls = ref<Record<string, string>>({});

  const clearUrl = sanitizeUrl(path, context.i18n.locale);
  Logger.debug('useMagentoMetaData url:', clearUrl);

  const load = async () => {
    try {
      const { data } = await context.app.$vsf.$magento.api.customQuery({
        query: urlRewriteStoreViews,
        queryVariables: { url: clearUrl },
      });

      const response = (data as any)?.urlRewriteStoreViews ?? null;
      Logger.debug('useMagentoMetaData response:', response);

      const storeConst = {
        1: 'pl',
        2: 'en',
      };

      magentoMetaUrls.value = response?.relative_urls?.reduce((acc: Record<string, string>, item: any) => {
        const key = storeConst[item.store_id];
        if (key) {
          const value = item.url;
          return { ...acc, [key]: value };
        }
        return acc;
      }, {}) || null;
    } catch (error) {
      Logger.error('useMagentoMetaData error:', error);
    }
  };

  function getMagentoAlternate(
    urls: Record<string, string> | null | undefined,
    storeUrl: string,
  ): Alternate[] {
    return Object.entries(urls || {})
      .filter(([lang]) => lang !== context.i18n.locale)
      .map(([lang, url]) => ({
        hreflang: lang,
        href: `${storeUrl}/${lang}/${url}`,
      }));
  }

  const alternate = computed(() => getMagentoAlternate(magentoMetaUrls.value, STORE_URL));
  const xDefault = computed(() => `${STORE_URL}/pl/${magentoMetaUrls.value.pl ?? ''}`);

  return { alternate, xDefault, load };
}
